import { tap } from "rxjs/operators";
import { of } from "rxjs";
import { isPlatformServer, isPlatformBrowser } from "@angular/common";
import { makeStateKey, StateKey, TransferState, PLATFORM_ID } from "@angular/core";

interface TransferableHost {
  transferState: TransferState;
  platformId: object;
}

declare global {
  interface JSON {
    decycle(object: unknown, replacer?: (value: unknown) => unknown): unknown;
    retrocycle($: unknown): unknown;
  }
}

export function Transferable(token: string) {
  console.log("@Transferable Evaluating ...", { token });

  return (
    target: object,
    propertyKey: string | symbol,
    descriptor: PropertyDescriptor,
  ): PropertyDescriptor | void => {
    const method = descriptor.value;

    const key = makeStateKey(token);

    console.log("@Transferable Running ...", { token, key });

    descriptor.value = function (...args: unknown[]) {
      const self = this as TransferableHost;
      const state = self.transferState.get(key, null);

      if (!state) {
        console.log("@Transferable : No state ! Call method ...", {
          token,
          state,
        });
        return method.apply(this, args).pipe(
          tap((data: unknown) => {
            if (isPlatformServer(self.platformId)) {
              console.log(
                "@Transferable : On server -> set state for " + token,
              );
              self.transferState.set(key, JSON.decycle(data));
            } else if (isPlatformBrowser(self.platformId)) {
              console.log("@Transferable : On browser -> do nothing");
            } else {
              console.warn("@Transferable : Plateform detection failed");
            }
          }),
        );
      } else {
        console.log("@Transferable : Found state ! Return cached value ...", {
          token,
          state,
        });
        self.transferState.remove(key);
        return of(JSON.retrocycle(state));
      }
    };

    return descriptor;
  };
}

if (typeof JSON.decycle !== "function") {
  JSON.decycle = function decycle(
    object: unknown,
    replacer?: (value: unknown) => unknown,
  ): unknown {
    "use strict";

    const objects = new WeakMap<object, string>(); // object to path mappings

    const derez = (value: unknown, path: string): unknown => {
      let oldPath: string | undefined; // The path of an earlier occurance of value
      let nu: unknown; // The new object or array

      if (replacer !== undefined) {
        value = replacer(value);
      }

      if (
        typeof value === "object" &&
        value !== null &&
        !(value instanceof Boolean) &&
        !(value instanceof Date) &&
        !(value instanceof Number) &&
        !(value instanceof RegExp) &&
        !(value instanceof String)
      ) {
        oldPath = objects.get(value as object);
        if (oldPath !== undefined) {
          return { $ref: oldPath };
        }

        objects.set(value as object, path);

        if (Array.isArray(value)) {
          nu = [];
          value.forEach((element: unknown, i: number) => {
            (nu as unknown[])[i] = derez(element, path + "[" + i + "]");
          });
        } else {
          nu = {};
          Object.keys(value as object).forEach((name: string) => {
            (nu as Record<string, unknown>)[name] = derez(
              (value as Record<string, unknown>)[name],
              path + "[" + JSON.stringify(name) + "]",
            );
          });
        }
        return nu;
      }
      return value;
    };

    return derez(object, "$");
  };
}

if (typeof JSON.retrocycle !== "function") {
  JSON.retrocycle = function retrocycle($: unknown): unknown {
    "use strict";

    const px =
      /^\$(?:\[(?:\d+|"(?:[^\\"\u0000-\u001f]|\\(?:[\\"\/bfnrt]|u[0-9a-zA-Z]{4}))*")\])*$/;

    const rez = (value: unknown): void => {
      if (value && typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach((element: unknown, i: number) => {
            if (typeof element === "object" && element !== null) {
              const path = (element as { $ref?: string }).$ref;
              if (typeof path === "string" && px.test(path)) {
                value[i] = window.eval(path);
              } else {
                rez(element);
              }
            }
          });
        } else {
          Object.keys(value as object).forEach((name: string) => {
            const item = (value as Record<string, unknown>)[name];
            if (typeof item === "object" && item !== null) {
              const path = (item as { $ref?: string }).$ref;
              if (typeof path === "string" && px.test(path)) {
                (value as Record<string, unknown>)[name] = window.eval(path);
              } else {
                rez(item);
              }
            }
          });
        }
      }
    };

    rez($);
    return $;
  };
}
